/**
 * Server autosave for deals that already have a file id (saved once).
 * Drafts still require explicit Save As — only updateDeal paths use this.
 */
import { validateInputs } from '../types/dealTypes';

/** Idle debounce before PUT /api/deals/:id (longer than calc debounce). */
export const SERVER_AUTOSAVE_MS = 1000;

/**
 * Build the payload shape sent to updateDeal (comment is UI-only, stripped).
 */
export function buildDealPersistPayload(
  inputs,
  assumptions,
  notes,
  cadastreData,
  marketData,
  fxSnapshot = null,
  scenarios = null,
  activeScenarioId = null,
  cashTracker = undefined,
  landAssessment = undefined,
  stageFinanceAssumptions = undefined,
) {
  const cleaned = validateInputs(inputs || {});
  const { comment, ...rest } = cleaned;
  const payload = {
    inputs: rest,
    assumptions: assumptions || {},
    notes: notes || '',
    cadastreData: cadastreData ?? null,
    marketData: marketData ?? null,
    fxSnapshot: fxSnapshot ?? null,
  };
  if (scenarios != null) {
    payload.scenarios = scenarios;
    payload.activeScenarioId = activeScenarioId || null;
  }
  if (cashTracker !== undefined) {
    payload.cashTracker = cashTracker;
  }
  if (landAssessment !== undefined) {
    payload.landAssessment = landAssessment;
  }
  if (stageFinanceAssumptions !== undefined) {
    payload.stageFinanceAssumptions = stageFinanceAssumptions;
  }
  return payload;
}

export function dealPersistFingerprint(payload) {
  return JSON.stringify(payload);
}

/**
 * Whether a scheduled server autosave should run.
 * Only when a file id exists and content differs from last successful persist.
 */
export function shouldServerAutosave(currentFileId, fingerprint, lastPersistedFingerprint) {
  if (!currentFileId || typeof currentFileId !== 'string' || !currentFileId.trim()) {
    return false;
  }
  if (!fingerprint) return false;
  return fingerprint !== lastPersistedFingerprint;
}

/**
 * One in-flight PUT at a time. A newer enqueue replaces the queued payload
 * so a slow save of version A cannot overwrite a later switch to version B.
 */
export function createSerialDealSaver() {
  const state = { inFlight: false, queued: null };
  async function enqueue(fileId, payload, send) {
    state.queued = { fileId, payload };
    if (state.inFlight) return { coalesced: true };
    state.inFlight = true;
    let last = { result: null, payload: null, fileId: null, coalesced: false };
    try {
      while (state.queued) {
        const job = state.queued;
        state.queued = null;
        const result = await send(job.fileId, job.payload);
        last = { result, payload: job.payload, fileId: job.fileId, coalesced: false };
      }
      return last;
    } finally {
      state.inFlight = false;
    }
  }
  return { enqueue };
}
